/**
 * Gộp "Kết cấu" + "Đóng gói" của BOM Mút xốp thành MỘT lựa chọn duy nhất ("Tháo rời - Ecom").
 * Chỉ liệt kê các cấu hình mã hàng thật sự có trong BOM:
 *  - đúng 1 cấu hình  -> tự chọn;
 *  - từ 2 cấu hình trở lên -> để trống, người dùng tự chọn;
 *  - 0 cấu hình -> báo chưa có BOM đóng gói.
 */

export type FoamAssembly = 'kd' | 'assembled'
export type FoamPackaging = 'thuong' | 'ecom'

export interface FoamConfigLike {
  assembly_type: string
  packaging_type: string
  status?: string | null
}

export interface FoamChoice {
  key: string
  assembly: FoamAssembly
  packaging: FoamPackaging
  /** Ví dụ "Tháo rời - Ecom". */
  label: string
  /** Cấu hình chưa hoàn thiện (trạng thái khác "OK"): vẫn chọn được nhưng báo cho người dùng. */
  incomplete: boolean
}

const ASSEMBLY_ORDER: FoamAssembly[] = ['kd', 'assembled']
const PACKAGING_ORDER: FoamPackaging[] = ['thuong', 'ecom']

export function foamChoiceKey(assembly: string, packaging: string): string {
  return `${assembly}|${packaging}`
}

function isAssembly(v: string): v is FoamAssembly {
  return v === 'kd' || v === 'assembled'
}
function isPackaging(v: string): v is FoamPackaging {
  return v === 'thuong' || v === 'ecom'
}

export function foamChoiceLabel(assembly: FoamAssembly, packaging: FoamPackaging): string {
  return productConfigLabel('foam', { assembly_type: assembly, packaging_type: packaging })
}

/** Gộp các cấu hình trùng (cùng kết cấu + đóng gói); ưu tiên bản "OK"; sắp xếp ổn định. */
export function buildFoamChoices(configs: FoamConfigLike[]): FoamChoice[] {
  const byKey = new Map<string, FoamChoice>()
  for (const c of configs) {
    if (!isAssembly(c.assembly_type) || !isPackaging(c.packaging_type)) continue
    const key = foamChoiceKey(c.assembly_type, c.packaging_type)
    const incomplete = (c.status ?? 'OK') !== 'OK'
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, {
        key,
        assembly: c.assembly_type,
        packaging: c.packaging_type,
        label: foamChoiceLabel(c.assembly_type, c.packaging_type),
        incomplete,
      })
    } else if (existing.incomplete && !incomplete) {
      existing.incomplete = false
    }
  }
  return [...byKey.values()].sort(
    (a, b) =>
      ASSEMBLY_ORDER.indexOf(a.assembly) - ASSEMBLY_ORDER.indexOf(b.assembly) ||
      PACKAGING_ORDER.indexOf(a.packaging) - PACKAGING_ORDER.indexOf(b.packaging),
  )
}

/** Đúng 1 lựa chọn thì tự lấy; 0 hoặc từ 2 trở lên thì không tự chọn. */
export function pickAutoFoamChoice(choices: FoamChoice[]): FoamChoice | null {
  return choices.length === 1 ? choices[0]! : null
}

export function findFoamChoice(
  choices: FoamChoice[],
  assembly: string | undefined,
  packaging: string | undefined,
): FoamChoice | null {
  if (!assembly || !packaging) return null
  return choices.find((c) => c.key === foamChoiceKey(assembly, packaging)) ?? null
}
import { productConfigLabel } from '@/lib/productConfigLabels'
